import type { GlyphIcon, IqTier } from './types';

/**
 * Cyclical palette used to give each topic a visual identity when the
 * backend doesn't ship per-domain colors. Order matches the design spec
 * (Identity → Storage → Compute → Networking → Monitor).
 */
export const DOMAIN_PALETTE: Array<{
  icon: GlyphIcon;
  gradient: [string, string];
  dot: string;
}> = [
  { icon: 'shield', gradient: ['#2563EB', '#4F46E5'], dot: '#4F46E5' },
  { icon: 'database', gradient: ['#0891B2', '#2563EB'], dot: '#0891B2' },
  { icon: 'cpu', gradient: ['#7C3AED', '#2563EB'], dot: '#7C3AED' },
  { icon: 'network', gradient: ['#F59E0B', '#D97706'], dot: '#F59E0B' },
  { icon: 'activity', gradient: ['#0D9488', '#16A34A'], dot: '#0D9488' },
];

/**
 * Per-domain Skill IQ tier; pass mark = 70 (matches the certification
 * `passing_score_percent` by default).
 *
 * Mirrors the `CM_iqTier` helper in the design source.
 */
export function getIqTier(mastery: number, passMark = 70): IqTier {
  if (mastery >= 80) return { key: 'expert', labelKey: 'dashboard.variantB.tier.expert', pass: true, barColor: 'green' };
  if (mastery >= passMark) return { key: 'prof', labelKey: 'dashboard.variantB.tier.proficient', pass: true, barColor: 'blue' };
  if (mastery >= passMark - 10) return { key: 'dev', labelKey: 'dashboard.variantB.tier.developing', pass: false, barColor: 'amber' };
  return { key: 'novice', labelKey: 'dashboard.variantB.tier.novice', pass: false, barColor: 'red' };
}

/** Map a tier bar color to Tailwind classes for the bar fill. */
export const BAR_FILL_CLASS: Record<IqTier['barColor'], string> = {
  green: 'bg-green-500',
  blue: 'bg-blue-600 dark:bg-blue-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

/** Map a tier to chip background / text classes. */
export const TIER_CHIP_CLASS: Record<IqTier['key'], string> = {
  expert: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
  prof: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  dev: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  novice: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};

/** Difficulty chip styling (matches design `.cm-chip--hard|med` + default). */
export const DIFF_CHIP_CLASS: Record<'easy' | 'med' | 'hard', string> = {
  easy: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
  med: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  hard: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};
